import { useEffect, useState } from 'react';
import { Form } from 'antd';
import { v4 as uuidv4 } from 'uuid';

import { useCalculateAdditionalToolScoreWithPostRoute, useGetAdditionalItemsOptions } from '@/hooks';
import type { AdditionalItem, GetAdditionalToolScoreDTO, GetAdditionalItemOptionsResponse } from '@/hooks';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useStepKeysContext } from '@/components/views/EwaJdsD86/Report/components/StepKeys/DefaultSelects/context';
import { worstRiskCalculator } from '@/components/views/EwaJdsD86/Report/components/StepKeys/AdditionalComponents/utils/WorstResultCalculator';
import type { Options } from '@/components/views/EwaJdsD86/Report/components/StepKeys/AdditionalComponents/types';

const { useFormInstance, useWatch } = Form;

const SELECT_OPTION_TYPE = 'select';
const RADIO_OPTION_TYPE = 'radio';

export enum FieldKey {
	OBJECT_DIMENSION = 'object_dimension',
	GLOVE_TYPE = 'glove_type',
	HEIGHT = 'height',
	WIDTH = 'width',
	DIAMETER = 'diameter'
}

const HAND_ARM_FIELDS: FieldKey[] = [
	FieldKey.OBJECT_DIMENSION,
	FieldKey.GLOVE_TYPE,
	FieldKey.HEIGHT,
	FieldKey.WIDTH,
	FieldKey.DIAMETER
];

const HEIGHT_AND_WIDTH_FIELDS: FieldKey[] = [FieldKey.HEIGHT, FieldKey.WIDTH];

const ACTION_TO_VISIBLE_FIELDS: Record<string, FieldKey[]> = {
	one_finger: HAND_ARM_FIELDS,
	two_fingers: HAND_ARM_FIELDS,
	flat_empty_hand: HAND_ARM_FIELDS,
	bent_thumb_empty_hand: HAND_ARM_FIELDS,
	clenched_fist: HAND_ARM_FIELDS,
	hand_plus_25mm_object: HAND_ARM_FIELDS,
	hand_plus_x_to_wrist: HAND_ARM_FIELDS,
	arm_to_elbow: HAND_ARM_FIELDS,
	arm_to_shoulder: HAND_ARM_FIELDS,
	both_arms_to_shoulder: HAND_ARM_FIELDS,
	leg_width: [FieldKey.WIDTH],
	foot_clearance: HEIGHT_AND_WIDTH_FIELDS,
	kneeling: HEIGHT_AND_WIDTH_FIELDS,
	squatting: HEIGHT_AND_WIDTH_FIELDS
};

interface UseSelectDependentInputsProps {
	additionalItem: AdditionalItem;
}

interface UseSelectDependentInputsReturn {
	formFieldName: string[];
	localAdditionalItem: AdditionalItem;
	selectOptions: GetAdditionalItemOptionsResponse[];
	radioOptions: GetAdditionalItemOptionsResponse[];
	isOptionsLoading: boolean;
	isFieldVisible: (fieldKey: FieldKey) => boolean;
	filterOption: (input: string, option?: Options) => boolean;
	handleChangeSelect: () => void;
	handleClearSelect: () => void;
	handleAddAdditionalItemValueClick: () => Promise<void>;
	handleRemoveAdditionalItemClick: (removedId: string) => void;
	isAddDisabled: boolean;
	hasAddedValues: boolean;
	worstRisk: number;
}

export function useSelectDependentInputs({
	additionalItem
}: Readonly<UseSelectDependentInputsProps>): UseSelectDependentInputsReturn {
	const { id } = additionalItem;

	const form = useFormInstance();
	const { formFieldName, stepKey } = useStepKeysContext();
	const { organization, company } = useApplicationContext();

	const [localAdditionalItem, setLocalAdditionalItem] = useState<AdditionalItem>(additionalItem);

	const formAction = useWatch([...formFieldName, 'additional_items', id, 'additional_item_option_id_1']);
	const formGloveType = useWatch([...formFieldName, 'additional_items', id, 'additional_item_option_id_2']);
	const formObjectDimension = useWatch([...formFieldName, 'additional_items', id, 'value_1']);
	const formHeight = useWatch([...formFieldName, 'additional_items', id, 'value_2']);
	const formWidth = useWatch([...formFieldName, 'additional_items', id, 'value_3']);
	const formDiameter = useWatch([...formFieldName, 'additional_items', id, 'value_4']);

	useEffect(() => {
		form.setFieldValue([...formFieldName, 'additional_items', 'local_values'], additionalItem);
	}, []);

	const { mutateAsync: calculateAdditionalToolScore, isLoading: isCalculatingScore } =
		useCalculateAdditionalToolScoreWithPostRoute();
	const { data: options, isLoading: isOptionsLoading } = useGetAdditionalItemsOptions({
		company_id: company?.id,
		organization_id: organization?.id,
		custom_report_step_key_id: stepKey.id
	});

	const selectOptions = options?.filter((option) => option.type === SELECT_OPTION_TYPE) ?? [];
	const radioOptions = options?.filter((option) => option.type === RADIO_OPTION_TYPE) ?? [];

	const selectedActionName = selectOptions.find(({ id: optionId }) => optionId === formAction)?.name;
	const visibleFields = selectedActionName ? (ACTION_TO_VISIBLE_FIELDS[selectedActionName] ?? []) : [];

	const worstRisk = worstRiskCalculator.getWorstResult(localAdditionalItem.results);

	const hasAddedValues = (localAdditionalItem?.results?.length ?? 0) > 0;

	function isFieldVisible(fieldKey: FieldKey): boolean {
		return visibleFields.includes(fieldKey);
	}

	function filterOption(input: string, option?: Options): boolean {
		return (option?.label.toLowerCase() ?? '').includes(input.toLowerCase());
	}

	function handleChangeSelect(): void {
		form.setFieldValue([...formFieldName, 'additional_items', id, 'additional_item_option_id_2'], undefined);
		form.setFieldValue([...formFieldName, 'additional_items', id, 'value_1'], undefined);
		form.setFieldValue([...formFieldName, 'additional_items', id, 'value_2'], undefined);
		form.setFieldValue([...formFieldName, 'additional_items', id, 'value_3'], undefined);
		form.setFieldValue([...formFieldName, 'additional_items', id, 'value_4'], undefined);
	}

	function handleClearSelect(): void {
		form.resetFields([...formFieldName, 'additional_items', id]);
	}

	function hasSomeEmptyField(): boolean {
		if (!formAction) {
			return true;
		}

		const fieldValuePairs = [
			{ field: FieldKey.OBJECT_DIMENSION, value: formObjectDimension },
			{ field: FieldKey.GLOVE_TYPE, value: formGloveType },
			{ field: FieldKey.HEIGHT, value: formHeight },
			{ field: FieldKey.WIDTH, value: formWidth },
			{ field: FieldKey.DIAMETER, value: formDiameter }
		];

		const hasEmptyVisibleField = fieldValuePairs.some(({ field, value }) => isFieldVisible(field) && !value);

		return hasEmptyVisibleField;
	}

	async function handleAddAdditionalItemValueClick(): Promise<void> {
		const payload: GetAdditionalToolScoreDTO = {
			value_1: formObjectDimension,
			value_2: formHeight,
			value_3: formWidth,
			value_4: formDiameter,
			company_id: company?.id,
			organization_id: organization?.id,
			custom_report_step_key_id: stepKey.id,
			additional_item_option_id_1: formAction,
			additional_item_option_id_2: formGloveType
		};

		const additionalToolScore = await calculateAdditionalToolScore(payload);

		if (!additionalToolScore?.result) {
			return;
		}

		const actionDescription = selectOptions.find(({ id: optionId }) => optionId === formAction)?.description;

		const newAdditionalItemValue = {
			id: uuidv4(),
			value_1: formObjectDimension,
			value_2: formHeight,
			value_3: formWidth,
			value_4: formDiameter,
			additional_item_option_id_1: formAction,
			additional_item_option_id_2: formGloveType,
			result: additionalToolScore.result,
			description: actionDescription
		};

		const previousResults = localAdditionalItem?.results ?? [];
		const nextResults = [...previousResults, newAdditionalItemValue];

		setLocalAdditionalItem({
			...localAdditionalItem,
			results: nextResults
		});

		form.setFieldValue([...formFieldName, 'additional_items', 'local_values', 'results'], nextResults);
		form.setFieldValue([...formFieldName, 'additional_items', id], undefined);
	}

	function handleRemoveAdditionalItemClick(removedId: string): void {
		const nextResults = localAdditionalItem.results.filter((item) => item.id !== removedId);

		setLocalAdditionalItem({
			...localAdditionalItem,
			results: nextResults
		});

		form.setFieldValue([...formFieldName, 'additional_items', 'local_values', 'results'], nextResults);
	}

	const isAddDisabled = hasSomeEmptyField() || isCalculatingScore;

	return {
		formFieldName,
		localAdditionalItem,
		selectOptions,
		radioOptions,
		isOptionsLoading,
		isFieldVisible,
		filterOption,
		handleChangeSelect,
		handleClearSelect,
		handleAddAdditionalItemValueClick,
		handleRemoveAdditionalItemClick,
		isAddDisabled,
		hasAddedValues,
		worstRisk
	};
}
